import React, { useEffect, useMemo, useState } from 'react';
import { pickBy } from 'lodash';
import Table from 'partial/components/Table';
import SearchBar from 'partial/components/input/SearchBar';
import Pagination from 'partial/components/Pagination';
import ListPanel from 'partial/components/ListPanel';
import SwitchToggle from 'partial/components/SwitchToggle';
import Badges from 'modules/common/components/Badge';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import Button from 'partial/components/Button';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import { aclHelper } from 'helper';

import { useMaskNameList } from './hooks';
import { useOpenActiveMaskName } from './modals/ActiveMaskName';
import { useOpenDeleteMaskName } from './modals/DeleteMaskName';
import { useOpenSendMessage } from './modals/SendMessage';

const INITIAL_STATE = {
  keyword: '',
};

export function Subscriptions() {
  const { setSubLabel } = useModuleContext();

  const [filter, setFilter] = useState(INITIAL_STATE);
  const [page, setPage] = useState({ page: '' });
  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
      }),
    [filter, page],
  );

  useEffect(() => {
    setPage({ page: '' });
  }, [filter]);

  const [isLoading, list, meta] = useMaskNameList(payload);
  const openActiveMaskName = useOpenActiveMaskName();
  const openDeleteMaskName = useOpenDeleteMaskName();
  const openSendMessage = useOpenSendMessage();

  const canDelete = aclHelper('sender_name', 'd');
  const canSendMessage = aclHelper('sender_name', 'w');
  const canActivate = aclHelper('sender_name', 'u');

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} mask name subscription${
            meta?.total === 1 ? '' : 's'
          }`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  const renderAction = (data) => (
    <div className="flex justify-end gap-2">
      {canDelete && (
        <Button
          icon={Trash}
          outline
          size="sm"
          onClick={() => openDeleteMaskName(data)}
        >
          Delete
        </Button>
      )}
      {canSendMessage && (
        <Button
          icon={PencilSimple}
          outline
          size="sm"
          onClick={() => openSendMessage(data)}
          disabled={data?.status?.toUpperCase() === 'REJECTED'}
        >
          Send message
        </Button>
      )}
      {canActivate && (
        <SwitchToggle
          value={!data?.is_deactivate}
          onChange={() => openActiveMaskName(data)}
        />
      )}
    </div>
  );

  const format = [
    { label: 'MASK NAME', key: 'name' },
    {
      label: 'AGENCY',
      key: 'organization_name',
      contentClassName: 'text-neutral-500',
    },
    {
      label: 'START DATE',
      key: 'subscribed_at',
      contentClassName: 'tabular-nums text-neutral-500',
    },
    {
      label: 'END DATE',
      key: 'expired_at',
      contentClassName: 'tabular-nums text-neutral-500',
    },
    {
      id: 'status',
      key: ({ status }) => <Badges status={status} />,
      label: 'STATUS',
      labelClassName: 'whitespace-nowrap',
    },
    ...(canDelete || canSendMessage || canActivate
      ? [
          {
            id: 'action',
            key: (data) => renderAction(data),
            label: 'ACTION',
            labelClassName: 'whitespace-nowrap',
            contentClassName: 'whitespace-nowrap',
          },
        ]
      : []),
  ];

  return (
    <ListPanel
      filters={
        <SearchBar name="keyword" value={filter.keyword} onChange={setFilter} />
      }
      summary={meta?.total ? `${list.length} of ${meta.total} shown` : null}
      pagination={
        +meta?.last_page > 1 ? (
          <Pagination value={meta} onChange={setPage} />
        ) : null
      }
    >
      <Table
        isLoading={isLoading}
        data={list}
        format={format}
        emptySpiel="No subscriptions match these filters."
      />
    </ListPanel>
  );
}

export default Subscriptions;
